/*
 * A teamBase permalink drawn as what it points at (X-A4 N10b). Somebody pastes
 * `http://host/c/allgemein/p/7` into a message; the link itself stays where they wrote it, and
 * under the body the message it names appears as a quoted card — author, picture, the first two
 * lines, the place, the time — so a reader does not have to leave the conversation to find out what
 * is being talked about.
 *
 * Four rules shape it, and each closes a way this feature goes wrong elsewhere.
 *
 * **Nothing is fetched that the reader may not read.** There is no new route and no new permission:
 * the card is resolved from the reader's own model first, and when the message is not in the loaded
 * window it is read through the permalink route the jump itself uses
 * (`chat.message.read`). A refusal is not an error state — it is the absence of a card, which is
 * exactly what a reader who may not see the message should get.
 *
 * **The body is the markdown's first two lines, as text.** Not the render tree: a card that drew the
 * tree would have to mount `MessageBody`, which mounts this file, and a permalink inside a quoted
 * message would then draw a card inside a card. Two lines of source cannot recurse at all, which is
 * the cheapest possible spelling of "never recursive".
 *
 * **At most two cards per message.** A message pasting five links is a list of links, not five
 * quotations, and the two that are drawn are the first two.
 *
 * **A card is a block, so it stands after the body, never inside it.** The inline permalink node is
 * a chip in the middle of a paragraph; a `<div>` there would break the paragraph in every browser.
 * Slack's shape — the link in the sentence, the cards below it — is also the one that survives a
 * copy of the message, because the cards are chrome and the sentence is the message.
 */

import { useEffect, useState } from "react"
import { formatDate, formatDateTime, formatTime, isoDateTime } from "../../../core/i18n/format.ts"
import { useNow } from "../../../core/i18n/useNow.ts"
import { Avatar } from "../../../core/people/Avatar.tsx"
import type { ConversationId, Model } from "../../../core/store/model.ts"
import { Shared, useShared } from "../../../core/store/shared.ts"
import { client, useModel } from "../../../core/store/store.ts"
import { messageHref } from "../../../routes/links.ts"
import { fetchMessageBySeq } from "../conversation/api.ts"
import { goTo } from "../conversation/opening.ts"
import { conversationAddress } from "../conversation/title.ts"
import "./quotedMessage.css"

/**
 * What this file needs of a permalink node, and no more.
 *
 * The wire's `Permalink` carries `kind` and `ref` — the route segments of the address as it was
 * written — and leaves `conversationId` at 0 until a caller with the database fills it in, which
 * nothing does today (`internal/core/wire/markdown.go`). Core's hand-written mirror
 * (`core/render/nodes.ts`) declares all four fields, and every one of them is optional here, so a card
 * simply does not appear where the tree carries neither address. That
 * keeps this file honest against the tree as it actually arrives rather than against one
 * declaration of it.
 */
export interface PermalinkRef {
	conversationId?: number | undefined
	/** "c" for a channel-style address, "d" for a direct conversation's handle. */
	kind?: string | undefined
	/** The slug or the direct handle exactly as it stood in the link. */
	ref?: string | undefined
	seq: number
}

/** The shape of a node tree this file walks: the type, the permalink and the children. */
interface NodeLike {
	type: string
	perma?: PermalinkRef | undefined
	children?: readonly NodeLike[] | undefined
}

/** How many quotations one message draws. Past this a message is a list of links (X-A4 N10b). */
export const MAX_QUOTE_CARDS = 2

/** How many lines of the quoted message a card shows. */
const EXCERPT_LINES = 2

/**
 * The path a permalink addresses, or null when it carries no address this build can navigate to.
 * The `ref` *is* the address — `/c/<slug>/p/<seq>`, `/d/<handle>/p/<seq>` — so a chip links
 * correctly whether or not the reader is a member of what it names: the route answers with the
 * history and a *Join* banner, or with "nothing answers to this address", which is the honest
 * outcome either way. `slugs` is the render context's map and the fallback for a tree whose
 * `conversationId` a future server has resolved.
 */
export function permalinkTarget(perma: PermalinkRef, slugs: Record<number, string>): string | null {
	const ref = perma.ref
	if (ref !== undefined && ref !== "") {
		const area = perma.kind === "d" ? "d" : "c"
		return `/${area}/${encodeURIComponent(ref)}/p/${String(perma.seq)}`
	}
	const slug = slugs[perma.conversationId ?? 0]
	return slug === undefined || slug === ""
		? null
		: `/c/${encodeURIComponent(slug)}/p/${String(perma.seq)}`
}

/**
 * The permalinks a message quotes: the first `MAX_QUOTE_CARDS` of them, in the order they were
 * written, without a repeat — the same message linked twice is one card, because the second would
 * say nothing the first did not.
 */
export function quotedPermalinks(nodes: readonly NodeLike[]): PermalinkRef[] {
	const found: PermalinkRef[] = []
	const seen = new Set<string>()

	const walk = (list: readonly NodeLike[]): void => {
		for (const node of list) {
			if (found.length === MAX_QUOTE_CARDS) return
			if (node.type === "permalink" && node.perma !== undefined) {
				const perma = node.perma
				const id = `${perma.kind ?? ""}/${perma.ref ?? ""}/${String(perma.conversationId ?? 0)}/${String(perma.seq)}`
				if (!seen.has(id)) {
					seen.add(id)
					found.push(perma)
				}
			}
			if (node.children !== undefined) walk(node.children)
		}
	}
	walk(nodes)
	return found
}

/**
 * The conversation a permalink names as *this reader's* model resolves it, or 0 for one they are not
 * in. It is a scan over the reader's conversations rather than an index: a team has dozens of them,
 * the answer is a number, and a selector that returns a number re-renders nothing when the model
 * moves for another reason.
 */
function permalinkConversation(model: Model, perma: PermalinkRef): ConversationId {
	const known = perma.conversationId ?? 0
	if (known > 0 && model.conversations[known] !== undefined) return known
	const ref = perma.ref
	if (ref === undefined || ref === "") return 0
	for (const [id, slice] of Object.entries(model.conversations)) {
		const meta = slice.meta
		const hit = perma.kind === "d" ? meta.dmHandle === ref : meta.slug === ref
		if (hit) return Number(id)
	}
	return 0
}

/** What a card draws of the message it quotes; the model's row and the permalink route's both fit. */
interface Quoted {
	authorId?: number | undefined
	createdAt: number
	markdown: string
	deletedAt?: number | undefined
}

/**
 * Every message a card has read through the route, by conversation and seq — the answer itself, and
 * `null` for one the reader may not have.
 *
 * It is a cache because the timeline is a window: a row is unmounted the moment it scrolls out of
 * view and mounted again when it scrolls back, and two messages may quote the same one. Without this
 * the same message was asked for once per mount, which is a request per scroll. A refusal is cached
 * too: it is an answer about this reader, and asking again would not change it.
 *
 * It forgets the least recently read entry past `MAX_CACHED_QUOTES`, because the thing it is a cache
 * for has no end: a reader scrolling a year of history in a week-old tab meets a new quoted message
 * every few screens, and every one of them is a whole `MessageView` — body, tree and all — that
 * nothing else would ever take back. A `Map` keeps its insertion order, so re-reading an entry moves
 * it to the end and the oldest is the first key the iterator hands over.
 */
const answers = new Map<string, Promise<Quoted | null>>()

/** How many quoted messages one tab keeps. A few hundred is a long day of scrolling. */
export const MAX_CACHED_QUOTES = 300

/**
 * What the cards are told when one of the messages they quote changes.
 *
 * A cached answer is right until the message it describes is edited or taken back, and neither
 * reaches a card through the model: the message a card read through the route is by definition
 * outside the loaded window, so no row of it is patched and nothing re-renders. The events do reach
 * the reader — they are a member of that conversation — so the cache listens for them itself,
 * forgets the entry and says so, and the mounted cards ask again. A delete then answers with a
 * tombstone and the card goes, which is what somebody taking a message back is for.
 */
const changes = new Shared(0)
let stopTap: (() => void) | undefined

function watchMessages(): void {
	// The type is read as a string, as the pin store's tap does: this file must not depend on the
	// module's event union to hear an event the store answers on its own.
	stopTap ??= client.onEvent((event) => {
		const { type, data } = event as { type: string; data: { conv?: number; seq?: number } }
		if (type !== "chat.message.deleted" && type !== "chat.message.edited") return
		if (data.conv === undefined || data.seq === undefined) return
		if (!answers.delete(`${String(data.conv)}/${String(data.seq)}`)) return
		changes.set(changes.now() + 1)
	})
}

function readMessage(conv: ConversationId, seq: number): Promise<Quoted | null> {
	watchMessages()
	const id = `${String(conv)}/${String(seq)}`
	const held = answers.get(id)
	if (held !== undefined) {
		// Read again: it goes to the end of the order, so what is dropped below is what no card has
		// looked at for longest.
		answers.delete(id)
		answers.set(id, held)
		return held
	}
	const answer = fetchMessageBySeq(conv, seq)
		.then((found): Quoted | null => found.message)
		.catch(() => null)
	answers.set(id, answer)
	for (const oldest of answers.keys()) {
		if (answers.size <= MAX_CACHED_QUOTES) break
		answers.delete(oldest)
	}
	return answer
}

/** Forgets what the route answered. The tests' way back to a cold cache. */
export function forgetQuotedMessages(): void {
	answers.clear()
	changes.set(changes.now() + 1)
}

/** The first lines of a body, as text: a card is a way back to the message, not a copy of it. */
function excerpt(markdown: string): string {
	return markdown
		.split("\n")
		.map((line) => line.trim())
		.filter((line) => line !== "")
		.slice(0, EXCERPT_LINES)
		.join(" ")
}

/**
 * One quotation. It resolves in two steps and draws nothing until the second has answered: the
 * conversation from the model, then the message from the loaded window or — once, per card — from
 * the permalink route. A message that is gone, deleted, or written by the system leaves no card;
 * the link in the body above is then the whole answer, which is what it was before this feature.
 */
function QuotedMessage({ perma, active }: { perma: PermalinkRef; active: boolean }) {
	const conv = useModel((model) => permalinkConversation(model, perma))
	const loaded = useModel((model) =>
		conv === 0 ? undefined : model.messages[conv]?.rows.find((row) => row.seq === perma.seq),
	)
	const status = useModel((model) => (conv === 0 ? "" : (model.conversations[conv]?.status ?? "")))
	const meta = useModel((model) => (conv === 0 ? undefined : model.conversations[conv]?.meta))
	const users = useModel((model) => model.users)
	const meId = useModel((model) => model.me?.id ?? 0)
	// `undefined` = nothing asked yet, `null` = asked and answered with nothing. The two must be
	// distinguishable or a refusal would be retried for as long as the row is on screen.
	const [fetched, setFetched] = useState<Quoted | null | undefined>(undefined)
	const now = useNow()

	// A window that is still being fetched is not an answer: the conversation a card points into is
	// usually the one being read, and asking the route for a message that is about to arrive in the
	// page means one request per card on every cold open. A conversation nobody has opened is "cold"
	// and is asked at once, because its window is not coming on its own.
	const waiting = status === "loading" || status === "resyncPending"

	// Bumped when the cache hears that this message changed; the effect below then asks again.
	const generation = useShared(changes)

	useEffect(() => {
		if (conv === 0 || waiting || loaded !== undefined) return undefined
		let cancelled = false
		void readMessage(conv, perma.seq).then((answer) => {
			if (!cancelled) setFetched(answer)
		})
		return () => {
			cancelled = true
		}
	}, [conv, waiting, loaded, perma.seq, generation])

	const msg: Quoted | null | undefined = loaded ?? fetched
	if (msg === undefined || msg === null) return null
	if (msg.deletedAt !== undefined || msg.authorId === undefined || meta === undefined) return null

	const href = messageHref(meta, perma.seq) ?? permalinkTarget(perma, {})
	if (href === null) return null

	const authorId = msg.authorId
	const author = users[authorId]?.displayName ?? ""
	const text = excerpt(msg.markdown)
	// The same rule every other stamp in the timeline follows: the time inside today, the date
	// before it, and the exact moment in the title.
	const when =
		formatDate(msg.createdAt) === formatDate(now)
			? formatTime(msg.createdAt)
			: formatDate(msg.createdAt)

	return (
		<a
			className="tb-quote"
			href={href}
			// The row's rule, not the body's: everything a row offers is out of the tab sequence until
			// the roving stop is on that row (`ThreadFooter`, the author button, the hover bar). A card
			// per message that was always tabbable would double the walk down a timeline, which is the
			// thing P18 removed.
			tabIndex={active ? 0 : -1}
			onClick={(event) => {
				if (event.metaKey || event.ctrlKey || event.shiftKey) return
				event.preventDefault()
				goTo(href)
			}}
		>
			<span className="tb-quote__head">
				<Avatar name={author} id={authorId} size="xs" url={users[authorId]?.avatarUrl} />
				<span className="tb-quote__author">{author}</span>
				<span className="tb-quote__place">{conversationAddress(meta, users, meId)}</span>
				<time
					className="tb-quote__time"
					dateTime={isoDateTime(msg.createdAt)}
					title={formatDateTime(msg.createdAt)}
				>
					{when}
				</time>
			</span>
			{text === "" ? null : <span className="tb-quote__excerpt">{text}</span>}
		</a>
	)
}

/**
 * Every quotation of one message, in the order the links were written. It renders nothing at all for
 * a body without a permalink, which is almost every body — so the cost of this feature on an
 * ordinary message is one walk over a tree the renderer is walking anyway.
 */
export function QuotedMessages({
	nodes,
	active = true,
}: {
	nodes: readonly NodeLike[]
	/** True while the row this belongs to holds the list's roving tab stop (`useIsActiveRow`). */
	active?: boolean
}) {
	const cards = quotedPermalinks(nodes)
	if (cards.length === 0) return null
	return (
		<>
			{cards.map((perma) => (
				<QuotedMessage
					key={`${perma.kind ?? ""}/${perma.ref ?? ""}/${String(perma.seq)}`}
					perma={perma}
					active={active}
				/>
			))}
		</>
	)
}
